// Helper para generar el PDF del contrato (base64) a partir del cliente.
// Carga los datos mínimos necesarios de Supabase y delega el render a ContratoPDF.

import { pdf } from '@react-pdf/renderer'
import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import { ContratoPDF, type DatosContratoPDF } from '@/lib/pdfContrato'
import { determinarTipoContrato } from '@/lib/determinarTipoContrato'
import { urlToDataUrl } from '@/lib/imagenDataUrl'
import type { ConfiguracionEstudio } from '@/services/configuracionService'
import { preciosDeAsignaciones } from '@/services/serviciosService'

interface GenerarContratoArgs {
  supabase: SupabaseClient<Database>
  clienteId: string
  configEstudio: ConfiguracionEstudio | null
  /** Permiso verHonorariosCliente de quien genera el contrato. */
  puedeVerHonorarios: boolean
}

export interface HonorariosContrato {
  precioImpositivo: number | null
  precioLaboral: number | null
  tieneModuloLaboral: boolean
  /** Todos los servicios activos del cliente los paga un pagador. */
  honorariosDeTercero: boolean
}

type ServicioContrato = {
  id: string
  frecuencia: string | null
  pagador_id: string | null
  servicios: { categoria: string | null } | null
}

/**
 * Arma los montos de la cláusula de honorarios con los servicios activos del cliente.
 * El módulo impositivo suma los servicios mensuales que no son de sueldos, con el criterio del lote:
 * la frecuencia es la del servicio del cliente y una frecuencia vacía cuenta como mensual.
 * El módulo laboral suma los servicios de sueldos.
 * Los servicios que paga un pagador no suman a ningún monto. tieneModuloLaboral cuenta todos los
 * servicios, también los de un pagador.
 * Los precios salen de vista_precios_servicios_cliente. Sin permiso de ver honorarios no se leen y
 * los dos montos quedan en null.
 */
export const cargarHonorariosContrato = async (
  supabase: SupabaseClient<Database>,
  clienteId: string,
  puedeVerHonorarios: boolean,
): Promise<HonorariosContrato> => {
  const { data } = await supabase
    .from('cliente_servicio_precios')
    .select('id, frecuencia, pagador_id, servicios ( categoria )')
    .eq('cliente_id', clienteId)
    .eq('activo', true)

  const servicios = (data ?? []) as unknown as ServicioContrato[]
  const esSueldos = (s: ServicioContrato) => s.servicios?.categoria === 'sueldos'
  const tieneModuloLaboral = servicios.some(esSueldos)
  const propios = servicios.filter((s) => !s.pagador_id)
  const honorariosDeTercero = servicios.length > 0 && propios.length === 0
  if (!puedeVerHonorarios) {
    return { precioImpositivo: null, precioLaboral: null, tieneModuloLaboral, honorariosDeTercero }
  }

  const precios = await preciosDeAsignaciones(supabase, propios.map((s) => s.id))
  const sumar = (lista: ServicioContrato[]) => lista.reduce((sum, s) => sum + (precios.get(s.id) ?? 0), 0)
  return {
    precioImpositivo: sumar(propios.filter((s) => !esSueldos(s) && (s.frecuencia ?? 'mensual') === 'mensual')),
    precioLaboral: tieneModuloLaboral ? sumar(propios.filter(esSueldos)) : null,
    tieneModuloLaboral,
    honorariosDeTercero,
  }
}

/**
 * El contrato es un documento legal y lleva la identidad del estudio en el encabezado y en la
 * firma. Antes esos campos caían a placeholders ("Mi Estudio Contable", "el titular", domicilio
 * vacío) y el PDF se enviaba igual al cliente — ORR-310. Ahora se corta acá.
 */
export class DatosEstudioIncompletosError extends Error {
  constructor(faltantes: string[]) {
    super(
      `Faltan datos del estudio para generar el contrato: ${faltantes.join(', ')}. ` +
        'Cargalos en Configuración → General.',
    )
    this.name = 'DatosEstudioIncompletosError'
  }
}

export async function generarContratoBase64({
  supabase,
  clienteId,
  configEstudio,
  puedeVerHonorarios,
}: GenerarContratoArgs): Promise<string> {
  // Antes de cualquier fetch: si el estudio no tiene identidad cargada, el contrato no se genera.
  const estudioNombre = configEstudio?.nombre?.trim() ?? ''
  const estudioResponsable = configEstudio?.responsable_nombre?.trim() ?? ''
  const estudioDireccion = configEstudio?.direccion?.trim() ?? ''

  const faltantes: string[] = []
  if (!estudioNombre) faltantes.push('nombre del estudio')
  if (!estudioResponsable) faltantes.push('responsable / titular')
  if (!estudioDireccion) faltantes.push('domicilio')
  if (faltantes.length > 0) throw new DatosEstudioIncompletosError(faltantes)

  // Cliente + persona
  const { data: cliente } = await supabase
    .from('clientes')
    .select('id, persona_id, fecha_alta')
    .eq('id', clienteId)
    .single()

  if (!cliente) throw new Error('Cliente no encontrado')

  if (!cliente.persona_id) {
    throw new Error('Cliente sin persona asociada')
  }

  const { data: persona, error: personaError } = await supabase
    .from('personas')
    .select(
      'nombre, apellido, nombre_completo, cuit, direccion, domicilio_calle, domicilio_numero, domicilio_localidad, domicilio_provincia, admin_nombre, admin_cuit, tipo',
    )
    .eq('id', cliente.persona_id)
    .single()

  if (personaError || !persona) {
    throw new Error('No se pudo cargar la persona del cliente')
  }

  const nombrePersona =
    persona.nombre_completo ||
    (persona.apellido ? `${persona.nombre ?? ''} ${persona.apellido}` : persona.nombre ?? '')
  const cuitPersona = persona.cuit ?? null
  const domicilioCalle = [persona.domicilio_calle, persona.domicilio_numero]
    .filter(Boolean)
    .join(' ')
  const domicilioPersona = domicilioCalle || persona.direccion || null
  const tipoPersona = persona.tipo ?? null

  // Faceta contable: tipo de contrato
  const { data: faceta } = await supabase
    .from('cliente_faceta_contable')
    .select('condicion_iva')
    .eq('cliente_id', clienteId)
    .maybeSingle()

  const tipo = determinarTipoContrato(tipoPersona, faceta?.condicion_iva ?? null)
  const honorarios = await cargarHonorariosContrato(supabase, clienteId, puedeVerHonorarios)

  const datos: DatosContratoPDF = {
    tipo,
    clienteNombre: nombrePersona || 'Cliente',
    clienteCuit: cuitPersona,
    clienteDomicilio: domicilioPersona,
    clienteCiudad: persona.domicilio_localidad ?? null,
    clienteProvincia: persona.domicilio_provincia ?? null,
    representanteNombre: persona.admin_nombre ?? null,
    representanteCuit: persona.admin_cuit ?? null,
    estudioNombre,
    estudioResponsable,
    estudioCuit: configEstudio?.cuit || '',
    estudioMatricula: configEstudio?.matricula_profesional || null,
    estudioDireccion,
    fechaAlta: cliente.fecha_alta || new Date().toISOString().slice(0, 10),
    ...honorarios,
  }

  const firmaUrl = await urlToDataUrl(configEstudio?.firma_email_imagen_url)
  const selloUrl = await urlToDataUrl(configEstudio?.firma_email_sello_url)

  const blob = await pdf(
    <ContratoPDF datos={datos} firmaUrl={firmaUrl} selloUrl={selloUrl} />,
  ).toBlob()
  const arrayBuffer = await blob.arrayBuffer()
  const bytes = new Uint8Array(arrayBuffer)
  // btoa-safe base64 encoding for binary data
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}
